import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ShieldCheck } from 'lucide-react';
import { EASE } from './Reveal';

const HERO_IMG =
  'https://images.unsplash.com/photo-1607336370849-990004549d6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxsb25kb24lMjBoZXJpdGFnZSUyMHRvd25ob3VzZSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MDYwNTUxNHww&ixlib=rb-4.1.0&q=85';

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: '115%' }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: '100%', label: 'Rent paid every month, occupied or not' },
  { value: '3–5+ yrs', label: 'Long-term lease agreements' },
  { value: '0', label: 'Void periods or tenant hassle' },
];

export default function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
      <div
        className="pointer-events-none absolute -right-40 top-0 h-[560px] w-[560px] rounded-full opacity-20"
        style={{ background: 'radial-gradient(circle, #DCBC7F 0%, transparent 65%)', filter: 'blur(90px)' }}
      />
      <div className="mx-auto grid max-w-7xl gap-16 px-6 pb-20 md:px-12 lg:grid-cols-12 lg:gap-8 lg:pb-28">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="mono-label mb-8 flex items-center gap-3 text-[#DCBC7F]"
            data-testid="hero-eyebrow"
          >
            <span className="brass-dot inline-block h-1.5 w-1.5 rounded-full bg-[#DCBC7F]" />
            Company Let Agreements · UK Wide
          </motion.p>
          <h1 className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-[#FCFBFA] sm:text-5xl lg:text-[4.4rem]">
            <Line delay={0.5}>Hassle-Free,</Line>
            <Line delay={0.65}>
              <em className="text-[#DCBC7F]">Guaranteed Rent.</em>
            </Line>
            <Line delay={0.8}>Your Property in Safe Hands.</Line>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-8 max-w-xl text-base font-normal leading-relaxed text-white sm:text-lg"
            data-testid="hero-subtext"
          >
            We lease high-quality properties directly from landlords to house corporate clients, working
            professionals, families on vacation and other trusted guests — a completely hands-off experience with
            guaranteed rental income, every month.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-enquiry-cta"
              onClick={() => onNavigate('#enquiry')}
              className="group flex items-center gap-2 rounded-full bg-[#DCBC7F] px-7 py-3.5 text-sm font-semibold text-[#155533] transition-colors duration-300 hover:bg-[#E9CD96]"
            >
              Schedule a Free Consultation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-explore-cta"
              onClick={() => onNavigate('#how-it-works')}
              className="rounded-full border border-[#F3E9D7]/25 px-7 py-3.5 text-sm font-medium text-[#F3E9D7] transition-colors duration-300 hover:border-[#DCBC7F]/60 hover:text-[#DCBC7F]"
            >
              Explore the Company Let Model
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-16 grid grid-cols-3 gap-4 border-t border-[#DCBC7F]/15 pt-8 sm:gap-8"
          >
            {STATS.map((s, i) => (
              <div key={s.label} data-testid={`hero-stat-${i}`}>
                <p className="font-display text-2xl font-semibold text-[#F3E9D7] sm:text-3xl">{s.value}</p>
                <p className="mt-1 text-[0.7rem] font-light leading-snug text-[#8DAF9C] sm:text-xs">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-t-[999px] rounded-b-2xl border border-[#DCBC7F]/25" aria-hidden="true" />
            <div className="relative h-[420px] overflow-hidden rounded-t-[999px] rounded-b-2xl sm:h-[520px] lg:h-[560px]">
              <motion.img
                src={HERO_IMG}
                alt="Georgian heritage townhouse, London"
                data-testid="hero-image"
                style={{ y: imgY, rotate: imgRotate }}
                className="h-[115%] w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#155533]/60 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.6, ease: EASE }}
              className="absolute -left-4 bottom-10 flex items-center gap-3 rounded-xl border border-[#DCBC7F]/25 bg-[#1B6640]/90 px-5 py-4 backdrop-blur-xl sm:-left-10"
              data-testid="hero-badge"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCBC7F]/15">
                <ShieldCheck size={18} className="text-[#DCBC7F]" />
              </span>
              <div>
                <p className="text-sm font-medium text-[#FCFBFA]">Rent on the 1st</p>
                <p className="text-xs font-light text-[#8DAF9C]">Occupied or not — guaranteed</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
